import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";

import NoDataMessage from "../../components/NoDataMessage/NoDataMessage.jsx";

const StackedBarChart = ({
  data = [],
  barSize = 50,
  height = 400,
  colors = {
    jobs: "#ad3bdd",
    resumes: "#B282F9",
    interviews: "#6B46C1",
  },
}) => {
  // No Data Detection
    const hasData =
      data &&
      data.length > 0 &&
      data.some(
        (item) =>
          (item.jobs ?? 0) > 0 ||
          (item.resumes ?? 0) > 0 ||
          (item.interviews ?? 0) > 0
      );

    if (!hasData) {
      return (
        <div
          className="flex items-center justify-center -mt-16 mb-16"
          style={{ height: `${height}px`, width: "100%" }}
        >
          <NoDataMessage />
        </div>
      );
    }

  // Calculate total activity (used for percentage labels)
  const totalActivity = data.reduce(
    (sum, item) => sum + item.jobs + item.resumes + item.interviews,
    0
  );

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        margin={{ top: 25, right: 30, left: 0, bottom: 120 }}
        barSize={barSize}
        barGap={0}
        barCategoryGap={0}
      >
        <CartesianGrid stroke="#333333" strokeDasharray="4 4" />

        <XAxis
          dataKey="shortName"
          angle={-45}
          textAnchor="end"
          height={130}
          tick={{ fill: "#e0e0e0", fontSize: 13 }}
          interval={0}
        />

        <YAxis tick={{ fill: "#e0e0e0", fontSize: 12 }} />

        <Tooltip
          cursor={{
            stroke: "#B794FF",
            strokeWidth: 0,
            strokeDasharray: "6 6",
            fill: "rgba(0, 0, 0, 0.38)",
          }}
          contentStyle={{
            background: "rgba(0,0,0,0.7)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "4px",
            padding: "12px 16px",
            backdropFilter: "blur(12px)",
          }}
          itemStyle={{
            color: "#fff",
            fontSize: "14px",
            fontWeight: 300,
          }}
          labelStyle={{
            color: "#fff",
            fontWeight: 400,
            fontSize: "15px",
            marginBottom: "6px",
          }}
          formatter={(value, name) => {
            const labelMap = {
              jobs: "Jobs Created",
              resumes: "Resumes Parsed",
              interviews: "Interviews Scheduled",
            };
            return [value, labelMap[name] || name];
          }}
        />

        {/* JOBS */}
        <Bar dataKey="jobs" stackId="stack" fill={colors.jobs}>
          <LabelList
            dataKey="shortName"
            position="bottom"
            offset={10}
            style={{
              fill: "#E0E0FF",
              fontSize: "12px",
              fontWeight: "light",
            }}
          />
        </Bar>

        {/* RESUMES */}
        <Bar dataKey="resumes" stackId="stack" fill={colors.resumes} />

        {/* INTERVIEWS */}
        <Bar dataKey="interviews" stackId="stack" fill={colors.interviews}>
          <LabelList
            dataKey={(entry) => {
              const total = entry.jobs + entry.resumes + entry.interviews;
              const percentage =
                totalActivity > 0
                  ? Math.round((total / totalActivity) * 100)
                  : 0;
              return percentage > 0 ? `${percentage}%` : "";
            }}
            position="top"
            style={{
              fill: "#E0E0FF",
              fontSize: "16px",
              fontWeight: "bold",
              textShadow: "0 2px 4px rgba(0,0,0,0.8)",
            }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default StackedBarChart;
